<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	title: 'Search results',
	placeholder: 'Search articles, topics, authors...',
	initialMessage: 'Enter a search term to find articles, topics, and authors.',
	noResultsMessage: 'No matching results.',
}
const schema = defineSchema({
	title: $.text('Title'),
	placeholder: $.text('Search Placeholder'),
	initialMessage: $.text('Initial Message'),
	noResultsMessage: $.text('No Results Message'),
})
export const BlockSearch = defineBlock({ thumbnail: '/block-thumbs/BlockSearch.svg', category: 'Listings', order: 260,
	name: 'BlockSearch',
	label: 'Search',
	icon: 'search',
	schema,
	defaults,
	dataSource: { collection: 'articles', store: 'the_torah', label: 'Articles' },
})
</script>
<script setup lang="ts">

import { Image, Loading, TextInput } from '@bagelink/vue'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSearchIndex } from '@/composables/useSearchIndex'
import { highlightMatches, queryTokens } from '@/utils/search'
import { displayImage } from '@/utils/image'




const props = defineProps<{
	title?: string
	placeholder?: string
	initialMessage?: string
	noResultsMessage?: string
}>()

const route = useRoute()
const router = useRouter()
const searchQuery = ref('')
const { load, search, loading: indexLoading, error: indexError } = useSearchIndex()

const tokens = computed(() => queryTokens(searchQuery.value))
const hasQuery = computed(() => searchQuery.value.trim().length > 0)
const results = computed(() => search(searchQuery.value))
const isSearching = computed(() => hasQuery.value && indexLoading.value)
const hasResults = computed(() =>
	results.value.articles.length > 0
	|| results.value.authors.length > 0
	|| results.value.topics.length > 0)

// The header search pushes `/search?q=…`; without this watcher the block kept the
// query it read on mount and the header bar appeared to do nothing once results
// were on screen. Keeping it route-driven also makes back/forward work.
watch(
	() => route.query.q,
	(value) => {
		const next = typeof value === 'string' ? value : ''
		if (next !== searchQuery.value) searchQuery.value = next
	},
	{ immediate: true },
)

// The index is a few MB — only fetch it once the visitor actually searches.
watch(
	searchQuery,
	(value) => {
		if (value.trim()) void load()
	},
	{ immediate: true },
)

function submit() {
	const value = searchQuery.value.trim()
	const current = typeof route.query.q === 'string' ? route.query.q : ''
	if (value === current) return
	void router.replace({ query: { ...route.query, q: value || undefined } })
}
</script>

<template>
	<section class="w720px mx-auto pt-4 pageHeight m_px-1 m_pt-2">
		<form role="search" @submit.prevent="submit">
			<TextInput
				v-model="searchQuery"
				:placeholder="props.placeholder"
				class="mb-0"
				icon="search"
			/>
		</form>

		<div class="pb-2">
			<h1 class="m-0 txt40 regular line-height-14 m_txt32 py-1-5 balance">
				{{ props.title }}
			</h1>

			<div v-if="isSearching" class="txt-center py-1 opacity-5">
				<Loading thickness="2" />
				<p class="pt-2">
					Searching...
				</p>
			</div>

			<template v-else-if="hasQuery && hasResults">
				<div v-if="results.topics.length" class="pb-2">
					<h2 class="m-0 txt21 semibold line-height-13 pb-1 m_txt17">
						Topics
					</h2>
					<div>
						<div v-for="topic in results.topics" :key="topic.s" class="pb-05">
							<router-link
								:to="`/topic/${topic.s}`"
								class="redhover decoration-none inline-flex align-items-center gap-05"
							>
								<span class="txt17 light" v-html="highlightMatches(topic.n, tokens)" />
							</router-link>
						</div>
					</div>
				</div>

				<div v-if="results.authors.length" class="pb-2">
					<h2 class="m-0 txt21 semibold line-height-13 pb-1 m_txt17">
						Authors
					</h2>
					<div>
						<div v-for="author in results.authors" :key="author.s" class="pb-1">
							<router-link
								:to="`/author/${author.s}`"
								class="hover display-flex gap-1 align-items-center decoration-none"
							>
								<Image
									v-if="author.i"
									:src="displayImage(author.i, 'thumb')"
									:alt="author.n"
									ratio="1"
								class="w40px h40px round cover flex-shrink-0"
								lazy
							/>
								<span class="display-flex column">
									<span class="txt17 medium color-primary">
										<span v-html="highlightMatches(author.n, tokens)" />
									</span>
									<span v-if="author.t || author.ins" class="txt15 light color-gray-70">
										{{ author.t }}<template v-if="author.t && author.ins">, </template>{{ author.ins }}
									</span>
								</span>
							</router-link>
						</div>
					</div>
				</div>

				<div v-if="results.articles.length">
					<h2 class="m-0 txt21 semibold line-height-13 pb-1 m_txt17">
						Articles
					</h2>
					<article
						v-for="result in results.articles"
						:key="result.article.k + result.article.s"
						class="pb-2 border-top pt-2"
					>
						<router-link :to="result.link" class="hover display-flex gap-1 align-items-start decoration-none">
							<Image
								:src="displayImage(result.article.i || '/og.jpg', 'card')"
								:alt="result.article.t"
								ratio="4-3"
							class="aspect-ratio-4-3 h-110px cover flex-shrink-0"
							lazy
						/>
							<div>
								<h3 class="m-0 regular color-primary line-height-13 balance">
									<span v-html="highlightMatches(result.article.t, tokens)" />
								</h3>
								<p class="color-gray-70 word-break-all m-0">
									www.thetorah.com{{ result.link }}
								</p>
								<p v-if="result.article.e" class="ellipsis-2 pt-025 pretty m-0">
									<span v-html="highlightMatches(result.article.e, tokens)" />
								</p>
								<p v-if="result.authors.length" class="pt-025 m-0 txt15 light color-gray-70">
									By
									<template v-for="(author, authorIndex) in result.authors" :key="author.s">
										{{ author.n }}<template v-if="authorIndex < result.authors.length - 1">, </template>
									</template>
								</p>
							</div>
						</router-link>
					</article>
				</div>
			</template>

			<p v-else-if="hasQuery">
				{{ indexError || props.noResultsMessage }}
			</p>

			<p v-else>
				{{ props.initialMessage }}
			</p>
		</div>
	</section>
</template>
